<template>
  <div>
    <b-card>
      <!-- Header -->
      <b-row class="mb-3">
        <b-col>
          <h3 class="mb-0">Teacher Approvals</h3>
          <p class="text-muted">Review demo lectures and approve new teachers</p>
        </b-col>
        <b-col class="text-right">
          <b-button variant="primary" @click="loadPending">
            <feather type="refresh-cw" class="feather-sm"></feather>
            Refresh
          </b-button>
        </b-col>
      </b-row>

      <!-- Stat -->
      <b-row class="mb-4">
        <b-col md="3">
          <b-card bg-variant="warning" text-variant="white">
            <h3 class="mb-0">{{ teachers.length }}</h3>
            <p class="mb-0">Pending Review</p>
          </b-card>
        </b-col>
      </b-row>

      <!-- Table -->
      <b-table
        :items="teachers"
        :fields="fields"
        :busy="loading"
        striped
        hover
        responsive
        show-empty
        empty-text="No teachers are waiting for review."
      >
        <template #table-busy>
          <div class="text-center my-5">
            <b-spinner class="align-middle mr-2"></b-spinner>
            <strong>Loading...</strong>
          </div>
        </template>

        <template #cell(name)="data">
          <div class="font-weight-bold">{{ data.item.name || 'Unnamed' }}</div>
          <small class="text-muted">{{ data.item.email }}</small>
        </template>

        <template #cell(phone)="data">
          {{ data.value || 'N/A' }}
        </template>

        <template #cell(created_on)="data">
          {{ formatDate(data.value) }}
        </template>

        <template #cell(actions)="data">
          <b-button variant="primary" size="sm" @click="openReview(data.item)">
            <feather type="play-circle" class="feather-sm mr-1"></feather>
            Review
          </b-button>
        </template>
      </b-table>
    </b-card>

    <!-- Review modal -->
    <b-modal
      id="teacherReviewModal"
      v-model="showModal"
      size="lg"
      title="Review demo lecture"
      hide-footer
    >
      <div v-if="selectedTeacher">
        <h5 class="mb-1">{{ selectedTeacher.name || 'Unnamed' }}</h5>
        <p class="text-muted mb-3">
          {{ selectedTeacher.email }}<span v-if="selectedTeacher.phone"> &middot; {{ selectedTeacher.phone }}</span>
        </p>

        <!-- Video -->
        <video
          v-if="selectedTeacher.demo_video_url"
          :src="selectedTeacher.demo_video_url"
          controls
          style="width: 100%; max-height: 440px; background: #000; border-radius: 6px;"
        ></video>
        <b-alert v-else show variant="warning">No demo video was uploaded.</b-alert>

        <!-- Rejection reason -->
        <b-form-group label="Rejection reason (required to reject)" class="mt-3">
          <b-form-textarea
            v-model="rejectReason"
            placeholder="e.g. Audio is unclear, please re-record"
            rows="2"
          ></b-form-textarea>
        </b-form-group>

        <div class="text-right mt-3">
          <b-button variant="secondary" class="mr-2" @click="showModal = false">Close</b-button>
          <b-button
            variant="danger"
            class="mr-2"
            :disabled="acting || !rejectReason.trim()"
            @click="reject"
          >
            <feather type="x-circle" class="feather-sm mr-1"></feather>
            Reject
          </b-button>
          <b-button variant="success" :disabled="acting" @click="approve">
            <feather type="check-circle" class="feather-sm mr-1"></feather>
            Approve
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import TeacherService from '@/services/teacher.service';
import moment from 'moment';

export default {
  name: 'TeacherApprovals',
  data() {
    return {
      teachers: [],
      loading: false,
      acting: false,
      showModal: false,
      selectedTeacher: null,
      rejectReason: '',
      fields: [
        { key: 'name', label: 'Teacher', sortable: true },
        { key: 'phone', label: 'Phone' },
        { key: 'created_on', label: 'Signed up', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
    };
  },
  created() {
    this.loadPending();
  },
  methods: {
    async loadPending() {
      this.loading = true;
      try {
        const response = await TeacherService.getPendingTeachers();
        this.teachers = response.data || [];
      } catch (error) {
        this.toast('Failed to load pending teachers', 'danger');
      } finally {
        this.loading = false;
      }
    },
    openReview(teacher) {
      this.selectedTeacher = teacher;
      this.rejectReason = '';
      this.showModal = true;
    },
    async approve() {
      this.acting = true;
      try {
        await TeacherService.approveTeacher(this.selectedTeacher.user_id);
        this.toast('Teacher approved', 'success');
        this.afterAction();
      } catch (error) {
        this.toast('Failed to approve teacher', 'danger');
      } finally {
        this.acting = false;
      }
    },
    async reject() {
      if (!this.rejectReason.trim()) return;
      this.acting = true;
      try {
        await TeacherService.rejectTeacher(this.selectedTeacher.user_id, this.rejectReason.trim());
        this.toast('Teacher rejected', 'success');
        this.afterAction();
      } catch (error) {
        this.toast('Failed to reject teacher', 'danger');
      } finally {
        this.acting = false;
      }
    },
    afterAction() {
      this.showModal = false;
      this.selectedTeacher = null;
      this.loadPending();
    },
    formatDate(date) {
      return date ? moment(date).format('MMM DD, YYYY') : 'N/A';
    },
    toast(message, variant) {
      this.$bvToast.toast(message, {
        title: variant === 'success' ? 'Success' : 'Error',
        variant,
        solid: true,
      });
    },
  },
};
</script>

<style scoped>
.feather-sm {
  width: 16px;
  height: 16px;
}
</style>
